<template>
  <div class="entity-list-page quote-builder-page">
    <header class="list-header">
      <div>
        <router-link to="/settings/marketing" class="back-link">← Marketing</router-link>
        <h1>Quote builder</h1>
        <p class="list-sub">
          Generate quotes and proposals for prospects — tiers, ROI, and competitive positioning.
        </p>
      </div>
      <div class="header-actions">
        <router-link to="/quotes" class="btn btn-ghost">Saved quotes</router-link>
      </div>
    </header>

    <!-- Quick Actions -->
    <div class="quick-actions card">
      <button @click="activeTab = 'simple'" :class="{ active: activeTab === 'simple' }" class="btn btn-outline">
        <i class="fas fa-bolt"></i> Quick Quote
      </button>
      <button @click="activeTab = 'proposal'" :class="{ active: activeTab === 'proposal' }" class="btn btn-outline">
        <i class="fas fa-file-contract"></i> Full Proposal
      </button>
      <button @click="activeTab = 'carrier'" :class="{ active: activeTab === 'carrier' }" class="btn btn-outline">
        <i class="fas fa-building"></i> Carrier Quote
      </button>
      <button @click="activeTab = 'compare'" :class="{ active: activeTab === 'compare' }" class="btn btn-outline">
        <i class="fas fa-balance-scale"></i> Compare Tiers
      </button>
      <button @click="activeTab = 'agent'" :class="{ active: activeTab === 'agent' }" class="btn btn-outline">
        <i class="fas fa-user-tie"></i> Agent Proposal
      </button>
    </div>

    <!-- Simple Quote Generator -->
    <div v-if="activeTab === 'simple'" class="quote-section card">
      <h3><i class="fas fa-bolt"></i> Quick Quote Generator</h3>
      
      <div class="form-grid">
        <div class="form-group">
          <label>Subscription Tier</label>
          <select v-model="simpleForm.tier" class="form-control">
            <option value="standard">Standard - $499/month</option>
            <option value="professional">Professional - $999/month</option>
            <option value="enterprise">Enterprise - Custom Pricing</option>
          </select>
        </div>

        <div class="form-group">
          <label>Billing Period</label>
          <select v-model="simpleForm.billing_period" class="form-control">
            <option value="monthly">Monthly</option>
            <option value="annual">Annual (17% discount)</option>
          </select>
        </div>

        <div class="form-group">
          <label>Number of Users</label>
          <input v-model.number="simpleForm.users" type="number" min="1" class="form-control" />
        </div>

        <div class="form-group">
          <label>Monthly Document Volume</label>
          <input v-model.number="simpleForm.monthly_doc_volume" type="number" min="1" class="form-control" />
        </div>
      </div>

      <div class="form-group">
        <label>Select Features</label>
        <div class="feature-selection">
          <div v-for="feature in availableFeatures" :key="feature.key" class="feature-checkbox">
            <label>
              <input type="checkbox" :value="feature.key" v-model="simpleForm.features" />
              <span class="feature-name">{{ feature.name }}</span>
              <span v-if="feature.core_offering" class="badge badge-primary">Core</span>
            </label>
            <p class="feature-description">{{ feature.description }}</p>
          </div>
        </div>
      </div>

      <div class="action-buttons">
        <button @click="generateQuote" :disabled="loading || simpleForm.features.length === 0" class="btn btn-primary btn-lg">
          <i v-if="loading" class="fas fa-spinner fa-spin"></i>
          <i v-else class="fas fa-calculator"></i>
          {{ loading ? 'Generating...' : 'Generate Quote' }}
        </button>
      </div>
    </div>

    <!-- Full Proposal Generator -->
    <div v-if="activeTab === 'proposal'" class="proposal-section card">
      <h3><i class="fas fa-file-contract"></i> Comprehensive Proposal Generator</h3>
      
      <div class="form-grid">
        <div class="form-group">
          <label>Client Name *</label>
          <input v-model="proposalForm.client_name" type="text" class="form-control" placeholder="e.g., Verus Insurance" />
        </div>

        <div class="form-group">
          <label>Industry</label>
          <input v-model="proposalForm.industry" type="text" class="form-control" placeholder="e.g., Insurance" />
        </div>

        <div class="form-group">
          <label>Number of Users</label>
          <input v-model.number="proposalForm.users" type="number" min="1" class="form-control" />
        </div>

        <div class="form-group">
          <label>Monthly Document Volume</label>
          <input v-model.number="proposalForm.monthly_doc_volume" type="number" min="1" class="form-control" />
        </div>

        <div class="form-group">
          <label>Billing Period</label>
          <select v-model="proposalForm.billing_period" class="form-control">
            <option value="monthly">Monthly</option>
            <option value="annual">Annual (17% discount)</option>
          </select>
        </div>
      </div>

      <div class="form-group">
        <label>Select Use Cases *</label>
        <div class="use-case-selection">
          <div v-for="feature in availableFeatures" :key="feature.key" class="use-case-card" 
               :class="{ selected: proposalForm.use_cases.includes(feature.key) }"
               @click="toggleUseCase(feature.key)">
            <div class="use-case-header">
              <input type="checkbox" :checked="proposalForm.use_cases.includes(feature.key)" @click.stop />
              <h4>{{ feature.name }}</h4>
              <span v-if="feature.core_offering" class="badge badge-primary">Core</span>
              <span class="competency-badge">{{ feature.competency_rating }}</span>
            </div>
            <p class="use-case-problem" v-if="feature.problem">
              <strong>Problem:</strong> {{ feature.problem }}
            </p>
            <p class="use-case-description">{{ feature.description }}</p>
            <div class="value-props">
              <strong>Value:</strong>
              <ul>
                <li v-for="(vp, idx) in feature.value_props.slice(0, 2)" :key="idx">{{ vp }}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div class="action-buttons">
        <button @click="generateProposal" :disabled="loading || !proposalForm.client_name || proposalForm.use_cases.length === 0" class="btn btn-success btn-lg">
          <i v-if="loading" class="fas fa-spinner fa-spin"></i>
          <i v-else class="fas fa-file-contract"></i>
          {{ loading ? 'Generating...' : 'Generate Proposal' }}
        </button>
      </div>
    </div>

    <!-- Carrier Quote Generator -->
    <div v-if="activeTab === 'carrier'" class="carrier-section card">
      <h3><i class="fas fa-building"></i> Carrier Quote Generator</h3>
      <p class="section-description">Generate carrier-specific quotes with builder support and document volume tiers</p>
      
      <div class="form-grid">
        <div class="form-group">
          <label>Carrier Name *</label>
          <input v-model="carrierForm.carrier_name" type="text" class="form-control" placeholder="e.g., Verus Insurance" />
        </div>

        <div class="form-group">
          <label>Package Tier</label>
          <select v-model="carrierForm.tier" class="form-control">
            <option value="small_builder">Small Builder - 75 docs/year ($3,500/year)</option>
            <option value="medium_builder">Medium Builder - 450 docs/year ($18,000/year)</option>
            <option value="large_builder">Large Builder - 2,200 docs/year ($75,000/year)</option>
            <option value="enterprise">Enterprise - Custom Pricing</option>
          </select>
        </div>

        <div class="form-group">
          <label>Annual Documents</label>
          <input v-model.number="carrierForm.annual_documents" type="number" min="1" class="form-control" />
        </div>

        <div class="form-group">
          <label>Number of Builders</label>
          <input v-model.number="carrierForm.num_builders" type="number" min="1" class="form-control" />
        </div>

        <div class="form-group">
          <label>Builder Support Model</label>
          <select v-model="carrierForm.builder_support_model" class="form-control">
            <option value="monthly_per_carrier">Monthly Per-Carrier ($500/month)</option>
            <option value="per_builder">Per-Builder Setup ($150/builder)</option>
            <option value="hybrid">Hybrid ($250/month + $75/builder)</option>
          </select>
        </div>

        <div class="form-group">
          <label>AI Model</label>
          <select v-model="carrierForm.use_premium_model" class="form-control">
            <option :value="false">Standard (Gemini Flash)</option>
            <option :value="true">Premium (Gemini Pro)</option>
          </select>
        </div>
      </div>

      <div class="action-buttons">
        <button @click="generateCarrierQuote" :disabled="loading || !carrierForm.carrier_name" class="btn btn-success btn-lg">
          <i v-if="loading" class="fas fa-spinner fa-spin"></i>
          <i v-else class="fas fa-building"></i>
          {{ loading ? 'Generating...' : 'Generate Carrier Quote' }}
        </button>
      </div>
    </div>

    <!-- Tier Comparison -->
    <div v-if="activeTab === 'compare'" class="comparison-section card">
      <h3><i class="fas fa-balance-scale"></i> Tier Comparison</h3>
      
      <div class="form-grid">
        <div class="form-group">
          <label>Number of Users</label>
          <input v-model.number="compareForm.users" type="number" min="1" class="form-control" />
        </div>

        <div class="form-group">
          <label>Monthly Document Volume</label>
          <input v-model.number="compareForm.monthly_doc_volume" type="number" min="1" class="form-control" />
        </div>
      </div>

      <div class="action-buttons">
        <button @click="generateComparison" :disabled="loading" class="btn btn-info btn-lg">
          <i v-if="loading" class="fas fa-spinner fa-spin"></i>
          <i v-else class="fas fa-balance-scale"></i>
          {{ loading ? 'Generating...' : 'Compare Tiers' }}
        </button>
      </div>
    </div>

    <!-- Agent Proposal Generator -->
    <div v-if="activeTab === 'agent'" class="agent-section card">
      <h3><i class="fas fa-user-tie"></i> Agent Proposal Generator</h3>
      <p class="section-description">
        Generate compelling proof-of-value proposals for insurance agents considering ADI. 
        Showcase real client success stories and demonstrate ROI with actual case study data.
      </p>
      
      <div class="form-grid">
        <div class="form-group">
          <label>Agent/Agency Name *</label>
          <input v-model="agentForm.agent_name" type="text" class="form-control" placeholder="e.g., Hilb Group" />
        </div>

        <div class="form-group">
          <label>Agent Contact Name</label>
          <input v-model="agentForm.contact_name" type="text" class="form-control" placeholder="e.g., John Smith" />
        </div>

        <div class="form-group">
          <label>Target Client Type</label>
          <select v-model="agentForm.client_type" class="form-control">
            <option value="franchise">Franchise (Multi-location)</option>
            <option value="builder">Builder/Contractor</option>
            <option value="general_contractor">General Contractor</option>
            <option value="property_manager">Property Manager</option>
            <option value="restaurant">Restaurant Chain</option>
          </select>
        </div>

        <div class="form-group">
          <label>Estimated # of Locations/Subjects</label>
          <input v-model.number="agentForm.num_subjects" type="number" min="1" class="form-control" />
        </div>

        <div class="form-group">
          <label>Annual Document Volume (Est.)</label>
          <input v-model.number="agentForm.annual_documents" type="number" min="1" class="form-control" />
        </div>

        <div class="form-group">
          <label>Case Study to Feature</label>
          <select v-model="agentForm.case_study" class="form-control">
            <option value="jersey_mikes">Jersey Mike's / Hilb Group (2,250+ locations)</option>
            <option value="builder_program">Builder Compliance Program</option>
            <option value="general">General Case Study</option>
          </select>
        </div>
      </div>

      <div class="form-group">
        <label>Key Pain Points to Address</label>
        <div class="pain-point-selection">
          <label class="checkbox-item">
            <input type="checkbox" value="manual_tracking" v-model="agentForm.pain_points" />
            <span>Manual COI tracking and renewals</span>
          </label>
          <label class="checkbox-item">
            <input type="checkbox" value="compliance_gaps" v-model="agentForm.pain_points" />
            <span>Compliance gaps discovered at claim time</span>
          </label>
          <label class="checkbox-item">
            <input type="checkbox" value="scaling" v-model="agentForm.pain_points" />
            <span>Difficulty scaling with client growth</span>
          </label>
          <label class="checkbox-item">
            <input type="checkbox" value="endorsement_verification" v-model="agentForm.pain_points" />
            <span>Endorsement and AI verification</span>
          </label>
          <label class="checkbox-item">
            <input type="checkbox" value="multi_location" v-model="agentForm.pain_points" />
            <span>Multi-location compliance management</span>
          </label>
          <label class="checkbox-item">
            <input type="checkbox" value="audit_trail" v-model="agentForm.pain_points" />
            <span>Audit trail and documentation</span>
          </label>
        </div>
      </div>

      <!-- Sample Check Definitions & Results Selection (Real Data) -->
      <div class="form-group sample-results-section">
        <label><i class="fas fa-clipboard-check"></i> Sample Check Definitions to Include</label>
        <p class="field-help">
          Select check definitions to showcase in the proposal with <strong>real anonymized analysis results</strong>. 
          This shows prospects actual evidence, pass/fail outcomes, and recommendations — without revealing internal logic.
          Only name, description, and type will be shared — no prompts or implementation details.
        </p>
        
        <div v-if="loadingCheckDefs" class="loading-indicator">
          <i class="fas fa-spinner fa-spin"></i> Loading available check definitions...
        </div>
        
        <div v-else-if="availableCheckDefinitions.length === 0" class="no-data-message">
          <i class="fas fa-info-circle"></i> No check definitions with completed analyses available.
        </div>
        
        <div v-else class="check-definition-selection sample-results-selection">
          <label v-for="checkDef in availableCheckDefinitions" :key="checkDef.id" class="checkbox-item check-def-item">
            <input type="checkbox" :value="checkDef.id" v-model="agentForm.sample_result_definition_ids" />
            <div class="check-def-info">
              <span class="check-def-name">{{ checkDef.name }}</span>
              <span class="check-def-meta">
                <span class="check-def-type">{{ formatCheckType(checkDef.check_application_type) }}</span>
                <span class="completed-runs">{{ checkDef.completed_runs }} completed runs</span>
              </span>
            </div>
          </label>
        </div>
        
        <p v-if="agentForm.sample_result_definition_ids.length > 0" class="field-note success-note">
          <i class="fas fa-check-circle"></i> {{ agentForm.sample_result_definition_ids.length }} check definition(s) selected. 
          Up to 5 sample results per definition will be included.
        </p>
        <p v-else class="field-note">
          <i class="fas fa-lightbulb"></i> Including sample results adds credibility by showing real outcomes.
        </p>
      </div>

      <div class="action-buttons">
        <button @click="generateAgentProposal" :disabled="loading || !agentForm.agent_name" class="btn btn-success btn-lg">
          <i v-if="loading" class="fas fa-spinner fa-spin"></i>
          <i v-else class="fas fa-file-contract"></i>
          {{ loading ? 'Generating...' : 'Generate Agent Proposal' }}
        </button>
      </div>
    </div>

    <!-- Results Display -->
    <div v-if="generatedQuote" class="results-section">
      <!-- Agent Proposal Display -->
      <div v-if="generatedQuote.proposal_type === 'agent_pov'" class="agent-proposal-results">
        <!-- Case Study Hero Section -->
        <div class="case-study-section">
          <div class="case-study-header">
            <h3><i class="fas fa-trophy"></i> Proof of Value: {{ generatedQuote.case_study.name }}</h3>
            <span class="case-study-badge">{{ generatedQuote.case_study.industry }}</span>
          </div>
          <p class="case-study-intro">
            Working with <strong>{{ generatedQuote.case_study.agent }}</strong>, we delivered exceptional results:
          </p>
          <div class="case-study-stats">
            <div class="case-study-stat">
              <span class="stat-value">{{ generatedQuote.case_study.stats.locations.toLocaleString() }}</span>
              <span class="stat-label">Locations</span>
            </div>
            <div class="case-study-stat">
              <span class="stat-value">{{ generatedQuote.case_study.stats.documents_processed.toLocaleString() }}</span>
              <span class="stat-label">Documents Processed</span>
            </div>
            <div class="case-study-stat">
              <span class="stat-value">{{ generatedQuote.case_study.stats.processing_time_days }}</span>
              <span class="stat-label">Days to Process</span>
            </div>
            <div class="case-study-stat">
              <span class="stat-value">{{ generatedQuote.case_study.stats.time_savings_percent }}%</span>
              <span class="stat-label">Time Savings</span>
            </div>
          </div>
          <div class="testimonial-box">
            <i class="fas fa-quote-left"></i>
            <p>"{{ generatedQuote.case_study.testimonial }}"</p>
            <span class="testimonial-author">— {{ generatedQuote.case_study.testimonial_author }}</span>
          </div>
        </div>

        <!-- Proposal Summary Card -->
        <div class="quote-summary card">
          <div class="summary-header">
            <h3>Proposal for {{ generatedQuote.agent_name }}</h3>
            <span class="quote-id">{{ generatedQuote.quote_id }}</span>
          </div>
          <div class="summary-grid">
            <div class="summary-item">
              <span class="label">Client Type</span>
              <span class="value">{{ generatedQuote.client_type_display }}</span>
            </div>
            <div class="summary-item">
              <span class="label">Locations/Subjects</span>
              <span class="value">{{ generatedQuote.prospect_profile.num_subjects.toLocaleString() }}</span>
            </div>
            <div class="summary-item">
              <span class="label">Annual Documents</span>
              <span class="value">{{ generatedQuote.prospect_profile.annual_documents.toLocaleString() }}</span>
            </div>
            <div class="summary-item">
              <span class="label">Estimated Annual</span>
              <span class="value">${{ generatedQuote.pricing.estimated_annual.toLocaleString() }}</span>
            </div>
            <div class="summary-item">
              <span class="label">ROI</span>
              <span class="value success">{{ generatedQuote.roi.roi_percentage }}%</span>
            </div>
            <div class="summary-item">
              <span class="label">Net Benefit (Year 1)</span>
              <span class="value success">${{ generatedQuote.roi.net_benefit_year1.toLocaleString() }}</span>
            </div>
          </div>
        </div>

        <!-- Pain Points Addressed -->
        <div v-if="generatedQuote.pain_points_addressed && generatedQuote.pain_points_addressed.length > 0" class="pain-points-section card">
          <h3><i class="fas fa-check-circle"></i> How We Address Your Challenges</h3>
          <div class="pain-point-cards">
            <div v-for="(pp, idx) in generatedQuote.pain_points_addressed" :key="idx" class="pain-point-card">
              <div class="pain-point-problem">
                <strong><i class="fas fa-exclamation-triangle"></i> Challenge:</strong>
                <p>{{ pp.problem }}</p>
              </div>
              <div class="pain-point-solution">
                <strong><i class="fas fa-lightbulb"></i> Solution:</strong>
                <p>{{ pp.solution }}</p>
              </div>
              <div class="pain-point-value">
                <strong><i class="fas fa-chart-line"></i> Value:</strong>
                <p>{{ pp.value }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Sample Check Definitions -->
        <div v-if="generatedQuote.sample_check_definitions && generatedQuote.sample_check_definitions.length > 0" class="check-definitions-section card">
          <h3><i class="fas fa-clipboard-check"></i> Sample Check Definitions</h3>
          <p class="section-intro">
            Below are sample check definitions we would configure for your compliance program. 
            We share the name, description, and type — <strong>not</strong> the underlying AI prompts or logic.
          </p>
          <div class="check-definitions-grid">
            <div v-for="(checkDef, idx) in generatedQuote.sample_check_definitions" :key="idx" class="check-definition-card">
              <div class="check-def-header">
                <h4>{{ checkDef.name }}</h4>
                <span class="check-type-badge">{{ formatCheckType(checkDef.check_type) }}</span>
              </div>
              <p class="check-def-description">{{ checkDef.description }}</p>
              <div class="sample-requirements">
                <strong>Sample Requirements:</strong>
                <ul>
                  <li v-for="(req, ridx) in checkDef.sample_requirements" :key="ridx">
                    <strong>{{ req.name }}:</strong> {{ req.description }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Sample Analysis Reports (Full Reports) -->
        <div v-if="generatedQuote.sample_check_runs && generatedQuote.sample_check_runs.length > 0" class="sample-results-section card">
          <h3><i class="fas fa-file-alt"></i> Sample Analysis Reports</h3>
          <p class="section-intro">
            Full comprehensive analysis reports from our platform. The PDF export will include detailed reports for each sample.
          </p>
          
          <div class="sample-reports-summary">
            <div v-for="(checkGroup, gidx) in generatedQuote.sample_check_runs" :key="gidx" class="check-run-group">
              <div class="check-run-header">
                <h4>{{ checkGroup.check_definition.name }}</h4>
                <span class="check-type-badge">{{ formatCheckType(checkGroup.check_definition.check_application_type) }}</span>
              </div>
              
              <div class="reports-grid">
                <div v-for="(sample, sidx) in checkGroup.sample_results.slice(0, 5)" :key="sidx" class="report-preview-card">
                  <div class="report-header">
                    <span class="report-number">Report #{{ gidx * 5 + sidx + 1 }}</span>
                    <span :class="['outcome-badge', sample.outcome?.toLowerCase()]">
                      {{ sample.outcome === 'PASS' ? 'COMPLIANT' : sample.outcome === 'FAIL' ? 'NON-COMPLIANT' : 'NEEDS REVIEW' }}
                    </span>
                  </div>
                  <div class="report-entity">
                    <i class="fas fa-building"></i> {{ sample.entity_name }}
                  </div>
                  <div class="report-stats">
                    <div class="stat">
                      <span class="stat-value">{{ sample.total_requirements }}</span>
                      <span class="stat-label">Checks</span>
                    </div>
                    <div class="stat passed">
                      <span class="stat-value">{{ sample.passed }}</span>
                      <span class="stat-label">Passed</span>
                    </div>
                    <div class="stat failed">
                      <span class="stat-value">{{ sample.failed }}</span>
                      <span class="stat-label">Failed</span>
                    </div>
                    <div class="stat">
                      <span class="stat-value">{{ sample.pass_rate }}%</span>
                      <span class="stat-label">Rate</span>
                    </div>
                  </div>
                  <div class="report-date">
                    <i class="fas fa-calendar"></i> {{ sample.analysis_date }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="sample-results-footer">
            <p><i class="fas fa-file-pdf"></i> <strong>PDF Export includes full detailed reports</strong> with evidence, reasoning, deficiencies, and recommendations for each requirement.</p>
            <p><i class="fas fa-user-shield"></i> Entity names anonymized to protect client confidentiality.</p>
          </div>
        </div>

        <!-- Pilot Process (NEW) -->
        <div v-if="generatedQuote.pilot_process" class="pilot-process-section card">
          <h3><i class="fas fa-flask"></i> {{ generatedQuote.pilot_process.title }}</h3>
          <p class="section-intro">{{ generatedQuote.pilot_process.description }}</p>
          
          <div class="pilot-steps">
            <div v-for="(step, idx) in generatedQuote.pilot_process.steps" :key="idx" class="pilot-step">
              <div class="step-number">{{ idx + 1 }}</div>
              <div class="step-content">
                <strong>{{ step.name }}</strong>
                <p>{{ step.description }}</p>
              </div>
            </div>
          </div>
          
          <div class="pilot-transparency-grid">
            <div class="transparency-box share">
              <h4><i class="fas fa-check-circle"></i> What We Share</h4>
              <ul>
                <li v-for="(item, idx) in generatedQuote.pilot_process.what_we_share" :key="idx">{{ item }}</li>
              </ul>
            </div>
            <div class="transparency-box protect">
              <h4><i class="fas fa-lock"></i> What We Protect</h4>
              <ul>
                <li v-for="(item, idx) in generatedQuote.pilot_process.what_we_protect" :key="idx">{{ item }}</li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Next Steps (Updated for new structured format) -->
        <div class="next-steps-section card">
          <h3><i class="fas fa-arrow-right"></i> Next Steps</h3>
          <!-- New structured format -->
          <div v-if="generatedQuote.next_steps && generatedQuote.next_steps.length > 0 && typeof generatedQuote.next_steps[0] === 'object'" class="structured-next-steps">
            <div v-for="(step, idx) in generatedQuote.next_steps" :key="idx" class="structured-step">
              <div class="step-header">
                <span class="step-badge">{{ step.step }}</span>
                <span class="step-title">{{ step.title }}</span>
                <span class="step-timeframe">{{ step.timeframe }}</span>
              </div>
              <ul class="step-actions">
                <li v-for="(action, aidx) in step.actions" :key="aidx">{{ action }}</li>
              </ul>
            </div>
          </div>
          <!-- Legacy simple format -->
          <ol v-else class="next-steps-list">
            <li v-for="(step, idx) in generatedQuote.next_steps" :key="idx">{{ step }}</li>
          </ol>
        </div>
      </div>

      <!-- Quote Summary Card (for non-agent proposals) -->
      <div v-else class="quote-summary card">
        <div class="summary-header">
          <h3>{{ generatedQuote.carrier_name || generatedQuote.client_name || 'Quote' }} Summary</h3>
          <span class="quote-id">{{ generatedQuote.quote_id }}</span>
        </div>

        <!-- Carrier-specific summary -->
        <div v-if="generatedQuote.carrier_name" class="summary-grid">
          <div class="summary-item">
            <span class="label">Package</span>
            <span class="value">{{ generatedQuote.tier }}</span>
          </div>
          <div class="summary-item">
            <span class="label">Annual Subscription</span>
            <span class="value">${{ generatedQuote.pricing.base_annual_subscription.toLocaleString() }}/year</span>
          </div>
          <div class="summary-item">
            <span class="label">Builder Support</span>
            <span class="value">${{ generatedQuote.pricing.builder_support.annual_total.toLocaleString() }}/year</span>
          </div>
          <div class="summary-item">
            <span class="label">Documents</span>
            <span class="value">{{ generatedQuote.document_volume.annual_documents.toLocaleString() }}/year</span>
          </div>
          <div class="summary-item">
            <span class="label">Builders</span>
            <span class="value">{{ generatedQuote.builders.count }}</span>
          </div>
          <div class="summary-item" v-if="generatedQuote.document_volume.client_cost_per_document">
            <span class="label">Cost per Document</span>
            <span class="value">${{ generatedQuote.document_volume.client_cost_per_document.toFixed(2) }}</span>
          </div>
          <div class="summary-item">
            <span class="label">Your Margin</span>
            <span class="value success">{{ generatedQuote.margin_analysis.margin_percentage }}%</span>
          </div>
          <div class="summary-item">
            <span class="label">Carrier ROI</span>
            <span class="value success">{{ generatedQuote.carrier_roi.roi_percentage }}%</span>
          </div>
          <div class="summary-item">
            <span class="label">Payback Period</span>
            <span class="value">{{ generatedQuote.carrier_roi.payback_months }} months</span>
          </div>
        </div>

        <!-- Standard summary -->
        <div v-else class="summary-grid">
          <div class="summary-item">
            <span class="label">Tier</span>
            <span class="value">{{ generatedQuote.tier }}</span>
          </div>
          <div class="summary-item">
            <span class="label">Subscription</span>
            <span class="value">{{ generatedQuote.billing.display }}</span>
          </div>
          <div class="summary-item">
            <span class="label">Setup Fee</span>
            <span class="value">${{ generatedQuote.billing.setup_fee.toLocaleString() }}</span>
          </div>
          <div class="summary-item">
            <span class="label">ROI</span>
            <span class="value success">{{ generatedQuote.roi.roi_percentage }}%</span>
          </div>
          <div class="summary-item">
            <span class="label">Payback Period</span>
            <span class="value">{{ generatedQuote.roi.payback_months }} months</span>
          </div>
          <div class="summary-item">
            <span class="label">Net Benefit (Year 1)</span>
            <span class="value success">${{ generatedQuote.roi.net_benefit_year1.toLocaleString() }}</span>
          </div>
        </div>
      </div>

      <!-- Cost Breakdown (not for agent proposals - they have their own ROI section) -->
      <div v-if="generatedQuote.proposal_type !== 'agent_pov'" class="cost-breakdown card">
        <h3><i class="fas fa-dollar-sign"></i> Cost Analysis</h3>
        
        <!-- Carrier Quote Cost Display -->
        <div v-if="generatedQuote.carrier_name" class="cost-section">
          <h4>API Costs (Annual)</h4>
          <div class="cost-grid">
            <div class="cost-item">
              <span>Model:</span>
              <span>{{ generatedQuote.model_selection.model_name }}</span>
            </div>
            <div class="cost-item">
              <span>Total Analyses:</span>
              <span>{{ generatedQuote.api_costs.total_analyses.toLocaleString() }}</span>
            </div>
            <div class="cost-item">
              <span>Input Cost:</span>
              <span>${{ generatedQuote.api_costs.input_cost_annual }}</span>
            </div>
            <div class="cost-item">
              <span>Output Cost:</span>
              <span>${{ generatedQuote.api_costs.output_cost_annual }}</span>
            </div>
            <div class="cost-item total">
              <span>Total Annual API Cost:</span>
              <span>${{ generatedQuote.api_costs.total_annual }}</span>
            </div>
            <div class="cost-item">
              <span>Monthly Average:</span>
              <span>${{ generatedQuote.api_costs.monthly_total }}</span>
            </div>
            <div class="cost-item">
              <span>Cost per Document:</span>
              <span>${{ generatedQuote.api_costs.cost_per_document }}</span>
            </div>
          </div>

          <div class="cost-section">
            <h4>Builder Support Costs</h4>
            <div class="cost-grid">
              <div class="cost-item">
                <span>Support Model:</span>
                <span>{{ generatedQuote.pricing.builder_support.model_name }}</span>
              </div>
              <div class="cost-item">
                <span>Monthly Cost:</span>
                <span>${{ generatedQuote.pricing.builder_support.monthly_cost }}</span>
              </div>
              <div class="cost-item">
                <span>Annual Cost:</span>
                <span>${{ generatedQuote.pricing.builder_support.annual_cost.toLocaleString() }}</span>
              </div>
              <div class="cost-item">
                <span>One-time Setup:</span>
                <span>${{ generatedQuote.pricing.builder_support.one_time_cost.toLocaleString() }}</span>
              </div>
              <div class="cost-item total">
                <span>Total Builder Support:</span>
                <span>${{ generatedQuote.pricing.builder_support.annual_total.toLocaleString() }}</span>
              </div>
            </div>
          </div>

          <div class="cost-section">
            <h4>Document Volume</h4>
            <div class="cost-grid">
              <div class="cost-item">
                <span>Included Documents:</span>
                <span>{{ generatedQuote.document_volume.included_documents.toLocaleString() }}/year</span>
              </div>
              <div class="cost-item" v-if="generatedQuote.document_volume.overage_documents > 0">
                <span>Overage Documents:</span>
                <span>{{ generatedQuote.document_volume.overage_documents }} × ${{ generatedQuote.document_volume.overage_rate }}</span>
              </div>
              <div class="cost-item" v-if="generatedQuote.document_volume.overage_cost > 0">
                <span>Overage Cost:</span>
                <span>${{ generatedQuote.document_volume.overage_cost.toLocaleString() }}</span>
              </div>
            </div>
          </div>

          <div class="margin-analysis">
            <h4>Your Margin Analysis</h4>
            <div class="margin-item">
              <span>Monthly Revenue:</span>
              <span>${{ generatedQuote.margin_analysis.monthly_revenue.toFixed(2) }}</span>
            </div>
            <div class="margin-item">
              <span>Monthly Operational Cost:</span>
              <span>${{ generatedQuote.margin_analysis.monthly_operational_cost.toFixed(2) }}</span>
            </div>
            <div class="margin-item success">
              <span>Gross Margin:</span>
              <span>${{ generatedQuote.margin_analysis.gross_margin_monthly.toFixed(2) }} ({{ generatedQuote.margin_analysis.margin_percentage }}%)</span>
            </div>
          </div>
        </div>

        <!-- Standard Quote Cost Display -->
        <div v-else>
          <div class="cost-section">
            <h4>API Costs (Monthly)</h4>
            <div class="cost-grid">
              <div class="cost-item">
                <span>Model:</span>
                <span>{{ generatedQuote.costs.api.model }}</span>
              </div>
              <div class="cost-item">
                <span>Total API Calls:</span>
                <span>{{ generatedQuote.costs.api.total_api_calls.toLocaleString() }}</span>
              </div>
              <div class="cost-item">
                <span>Input Cost:</span>
                <span>${{ generatedQuote.costs.api.input_cost }}</span>
              </div>
              <div class="cost-item">
                <span>Output Cost:</span>
                <span>${{ generatedQuote.costs.api.output_cost }}</span>
              </div>
              <div class="cost-item total">
                <span>Total Monthly API Cost:</span>
                <span>${{ generatedQuote.costs.api.total_monthly }}</span>
              </div>
            </div>

            <div v-if="generatedQuote.api_cost_by_use_case" class="use-case-costs">
              <h5>Cost by Use Case</h5>
              <div v-for="(cost, key) in generatedQuote.api_cost_by_use_case" :key="key" class="use-case-cost-item">
                <span class="feature-name">{{ cost.feature_name }}</span>
                <span class="cost-value">${{ cost.monthly_cost }}/mo</span>
                <span class="cost-detail">{{ cost.api_calls_per_month.toLocaleString() }} calls</span>
              </div>
            </div>
          </div>

          <div class="cost-section">
            <h4>Infrastructure Costs (Monthly)</h4>
            <div class="cost-grid">
              <div class="cost-item">
                <span>Base Shared:</span>
                <span>${{ generatedQuote.costs.infrastructure.base_shared }}</span>
              </div>
              <div class="cost-item">
                <span>Per Client:</span>
                <span>${{ generatedQuote.costs.infrastructure.per_client }}</span>
              </div>
              <div class="cost-item total">
                <span>Total Infrastructure:</span>
                <span>${{ generatedQuote.costs.infrastructure.total_monthly }}</span>
              </div>
            </div>
          </div>

          <div class="margin-analysis">
            <h4>Margin Analysis</h4>
            <div class="margin-item">
              <span>Total Monthly Cost:</span>
              <span>${{ generatedQuote.costs.total_monthly }}</span>
            </div>
            <div class="margin-item">
              <span>Monthly Revenue:</span>
              <span>${{ generatedQuote.billing.monthly_equivalent.toFixed(2) }}</span>
            </div>
            <div class="margin-item success">
              <span>Gross Margin:</span>
              <span>${{ generatedQuote.costs.gross_margin.toFixed(2) }} ({{ generatedQuote.costs.margin_percentage }}%)</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Use Case Analysis (for proposals) -->
      <div v-if="generatedQuote.use_case_analysis" class="use-case-analysis card">
        <h3><i class="fas fa-clipboard-check"></i> Use Case Analysis</h3>
        
        <div v-for="(uc, idx) in generatedQuote.use_case_analysis" :key="idx" class="use-case-detail">
          <h4>{{ uc.name }}</h4>
          
          <div class="problem-solution">
            <div class="problem">
              <strong>The Problem:</strong>
              <p>{{ uc.problem }}</p>
            </div>
            <div class="solution">
              <strong>Our Solution:</strong>
              <p>{{ uc.solution }}</p>
            </div>
          </div>

          <div class="value-delivered">
            <strong>Value Delivered:</strong>
            <ul>
              <li v-for="(vp, vpIdx) in uc.value_delivered" :key="vpIdx">{{ vp }}</li>
            </ul>
          </div>

          <div class="competitive-position">
            <strong>Competitive Position:</strong>
            <div class="comp-grid">
              <div class="comp-item">
                <span>Our Competency:</span>
                <span class="badge badge-success">{{ uc.our_competency }}</span>
              </div>
              <div v-if="uc.competitor_weakness" class="comp-item">
                <span>Competitor Weakness:</span>
                <span>{{ uc.competitor_weakness }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Implementation Timeline (for proposals - not agent proposals which have their own) -->
      <div v-if="generatedQuote.implementation_timeline && generatedQuote.proposal_type !== 'agent_pov'" class="timeline card">
        <h3><i class="fas fa-calendar-alt"></i> Implementation Timeline</h3>
        <p class="timeline-summary">
          Total Duration: <strong>{{ generatedQuote.implementation_timeline.total_weeks }} weeks</strong> 
          ({{ generatedQuote.implementation_timeline.total_months }} months)
        </p>
        
        <div class="timeline-phases">
          <div v-for="(phase, idx) in generatedQuote.implementation_timeline.phases" :key="idx" class="phase">
            <div class="phase-header">
              <h4>{{ phase.phase }}</h4>
              <span class="phase-duration">{{ phase.weeks }}</span>
            </div>
            <ul class="phase-activities">
              <li v-for="(activity, actIdx) in phase.activities" :key="actIdx">{{ activity }}</li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Export Options -->
      <div class="export-section card">
        <h3><i class="fas fa-download"></i> Export & Save Options</h3>
        <div class="export-buttons">
          <!-- Save as Draft - Primary Action for Agent Proposals -->
          <button v-if="generatedQuote.proposal_type === 'agent_pov' && !savedQuote" 
                  @click="saveQuoteAsDraft" 
                  :disabled="savingQuote"
                  class="btn btn-success">
            <i v-if="savingQuote" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-save"></i> 
            {{ savingQuote ? 'Saving...' : 'Save as Draft' }}
          </button>
          <button v-if="savedQuote" 
                  @click="editSavedQuote" 
                  class="btn btn-success">
            <i class="fas fa-edit"></i> Edit Saved Quote
          </button>
          
          <button v-if="generatedQuote.carrier_name" @click="exportPDF" class="btn btn-primary">
            <i class="fas fa-file-pdf"></i> Generate PDF Proposal
          </button>
          <button v-if="generatedQuote.proposal_type === 'agent_pov'" @click="exportAgentPDF" class="btn btn-primary">
            <i class="fas fa-file-pdf"></i> Generate PDF Proposal
          </button>
          <button @click="exportMarkdown" class="btn btn-outline">
            <i class="fas fa-file-markdown"></i> Export as Markdown
          </button>
          <button @click="copyToClipboard" class="btn btn-outline">
            <i class="fas fa-copy"></i> Copy Summary
          </button>
          <button @click="printQuote" class="btn btn-outline">
            <i class="fas fa-print"></i> Print
          </button>
        </div>
        
        <!-- Saved Quote Info -->
        <div v-if="savedQuote" class="saved-quote-info">
          <i class="fas fa-check-circle"></i>
          <span>Saved as <strong>{{ savedQuote.quote_number }}</strong></span>
          <router-link :to="`/quote-editor/${savedQuote.id}`" class="edit-link">
            Open in Editor →
          </router-link>
        </div>
        
        <p v-if="generatedQuote.carrier_name" class="export-note">
          <i class="fas fa-info-circle"></i> PDF Proposal includes all 5 problem statements, 10 questions, and implementation roadmap
        </p>
        <p v-if="generatedQuote.proposal_type === 'agent_pov'" class="export-note">
          <i class="fas fa-info-circle"></i> Save as Draft to edit content before exporting. PDF includes case study, sample check definitions, pilot process, and next steps.
        </p>
      </div>
    </div>

    <!-- Tier Comparison Results -->
    <div v-if="comparisonResults" class="comparison-results">
      <div class="comparison-grid">
        <div v-for="(item, idx) in comparisonResults.comparisons" :key="idx" class="tier-card card">
          <div class="tier-header">
            <h3>{{ item.quote.tier }}</h3>
            <span v-if="comparisonResults.recommendation === item.tier" class="badge badge-success">Recommended</span>
          </div>
          
          <div class="tier-pricing">
            <div class="price">{{ item.quote.billing.display }}</div>
            <div class="setup-fee">Setup: ${{ item.quote.billing.setup_fee.toLocaleString() }}</div>
          </div>

          <div class="tier-features">
            <h4>Features ({{ item.quote.features.length }})</h4>
            <ul>
              <li v-for="(feature, fIdx) in item.quote.features.slice(0, 5)" :key="fIdx">
                {{ feature.name }}
              </li>
              <li v-if="item.quote.features.length > 5">+ {{ item.quote.features.length - 5 }} more...</li>
            </ul>
          </div>

          <div class="tier-roi">
            <div class="roi-item">
              <span>ROI:</span>
              <span class="success">{{ item.quote.roi.roi_percentage }}%</span>
            </div>
            <div class="roi-item">
              <span>Payback:</span>
              <span>{{ item.quote.roi.payback_months }} months</span>
            </div>
            <div class="roi-item">
              <span>Net Benefit:</span>
              <span class="success">${{ item.quote.roi.net_benefit_year1.toLocaleString() }}</span>
            </div>
          </div>

          <button @click="selectTierFromComparison(item.quote)" class="btn btn-primary btn-block">
            Select This Tier
          </button>
        </div>
      </div>
    </div>

    <!-- Error Display -->
    <div v-if="error" class="error-message card">
      <h3><i class="fas fa-exclamation-triangle"></i> Error</h3>
      <p>{{ error }}</p>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import api from '@/services/api';

export default {
  name: 'QuoteBuilder',
  setup() {
    const activeTab = ref('simple');
    const loading = ref(false);
    const error = ref(null);
    
    const availableFeatures = ref([]);
    const subscriptionTiers = ref({});
    const availableCheckDefinitions = ref([]);  // Real check definitions from DB for sample results
    const loadingCheckDefs = ref(false);
    
    const simpleForm = ref({
      tier: 'professional',
      features: [],
      monthly_doc_volume: 200,
      users: 25,
      billing_period: 'annual'
    });
    
    const proposalForm = ref({
      client_name: '',
      industry: 'Insurance',
      use_cases: [],
      monthly_doc_volume: 300,
      users: 50,
      billing_period: 'annual'
    });
    
    const compareForm = ref({
      users: 25,
      monthly_doc_volume: 200
    });
    
    const carrierForm = ref({
      carrier_name: '',
      tier: 'medium_builder',
      annual_documents: 450,
      num_builders: 15,
      builder_support_model: 'hybrid',
      use_premium_model: false
    });
    
    const agentForm = ref({
      agent_name: '',
      contact_name: '',
      client_type: 'franchise',
      num_subjects: 100,
      annual_documents: 500,
      case_study: 'jersey_mikes',
      pain_points: ['manual_tracking', 'compliance_gaps'],
      sample_result_definition_ids: []  // Real DB IDs for check definitions & sample results
    });
    
    const generatedQuote = ref(null);
    const comparisonResults = ref(null);
    const savedQuote = ref(null);  // Reference to saved quote in DB
    const savingQuote = ref(false);
    
    // Load feature catalog
    const loadFeatureCatalog = async () => {
      try {
        const response = await api.get('/quote-generator/feature-catalog/');
        availableFeatures.value = response.data.features;
      } catch (err) {
        console.error('Error loading feature catalog:', err);
        error.value = 'Failed to load feature catalog';
      }
    };
    
    // Load subscription tiers
    const loadSubscriptionTiers = async () => {
      try {
        const response = await api.get('/quote-generator/subscription-tiers/');
        subscriptionTiers.value = response.data.tiers;
      } catch (err) {
        console.error('Error loading subscription tiers:', err);
      }
    };
    
    // Load available check definitions (with completed runs) for sample results
    const loadAvailableCheckDefinitions = async () => {
      loadingCheckDefs.value = true;
      try {
        const response = await api.get('/quote-generator/available-check-definitions/');
        availableCheckDefinitions.value = response.data.check_definitions || [];
      } catch (err) {
        console.error('Error loading check definitions:', err);
        // Not critical - sample results are optional
      } finally {
        loadingCheckDefs.value = false;
      }
    };
    
    // Generate simple quote
    const generateQuote = async () => {
      loading.value = true;
      error.value = null;
      generatedQuote.value = null;
      comparisonResults.value = null;
      
      try {
        const response = await api.post('/quote-generator/generate-quote/', simpleForm.value);
        generatedQuote.value = response.data;
      } catch (err) {
        console.error('Error generating quote:', err);
        error.value = err.response?.data?.error || 'Failed to generate quote';
      } finally {
        loading.value = false;
      }
    };
    
    // Generate comprehensive proposal
    const generateProposal = async () => {
      loading.value = true;
      error.value = null;
      generatedQuote.value = null;
      comparisonResults.value = null;
      
      try {
        const response = await api.post('/quote-generator/generate-proposal/', proposalForm.value);
        generatedQuote.value = response.data;
      } catch (err) {
        console.error('Error generating proposal:', err);
        error.value = err.response?.data?.error || 'Failed to generate proposal';
      } finally {
        loading.value = false;
      }
    };
    
    // Generate carrier quote
    const generateCarrierQuote = async () => {
      loading.value = true;
      error.value = null;
      generatedQuote.value = null;
      comparisonResults.value = null;
      
      try {
        const response = await api.post('/quote-generator/generate-carrier-quote/', carrierForm.value);
        generatedQuote.value = response.data;
      } catch (err) {
        console.error('Error generating carrier quote:', err);
        error.value = err.response?.data?.error || 'Failed to generate carrier quote';
      } finally {
        loading.value = false;
      }
    };
    
    // Generate agent proposal
    const generateAgentProposal = async () => {
      loading.value = true;
      error.value = null;
      generatedQuote.value = null;
      comparisonResults.value = null;
      
      try {
        const response = await api.post('/quote-generator/generate-agent-proposal/', agentForm.value);
        generatedQuote.value = response.data;
      } catch (err) {
        console.error('Error generating agent proposal:', err);
        error.value = err.response?.data?.error || 'Failed to generate agent proposal';
      } finally {
        loading.value = false;
      }
    };
    
    // Generate tier comparison
    const generateComparison = async () => {
      loading.value = true;
      error.value = null;
      generatedQuote.value = null;
      comparisonResults.value = null;
      
      try {
        const payload = {
          tiers: ['standard', 'professional', 'enterprise'],
          ...compareForm.value
        };
        const response = await api.post('/quote-generator/compare-tiers/', payload);
        comparisonResults.value = response.data;
      } catch (err) {
        console.error('Error generating comparison:', err);
        error.value = err.response?.data?.error || 'Failed to generate comparison';
      } finally {
        loading.value = false;
      }
    };
    
    // Toggle use case selection
    const toggleUseCase = (key) => {
      const index = proposalForm.value.use_cases.indexOf(key);
      if (index > -1) {
        proposalForm.value.use_cases.splice(index, 1);
      } else {
        proposalForm.value.use_cases.push(key);
      }
    };
    
    // Select tier from comparison
    const selectTierFromComparison = (quote) => {
      generatedQuote.value = quote;
      comparisonResults.value = null;
      activeTab.value = 'simple';
    };
    
    // Export to PDF (for carrier quotes)
    const exportPDF = async () => {
      try {
        const response = await api.post('/quote-generator/export-pdf/', {
          quote: generatedQuote.value
        });
        
        // Open HTML in new window for printing
        const printWindow = window.open('', '_blank');
        printWindow.document.write(response.data);
        printWindow.document.close();
        
        // Wait for content to load, then show print dialog
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            alert('PDF ready! Use your browser\'s print dialog to save as PDF.\n\nTip: Choose "Save as PDF" as the destination.');
          }, 500);
        };
      } catch (err) {
        console.error('Error generating PDF:', err);
        alert('Failed to generate PDF');
      }
    };
    
    // Export to PDF (for agent proposals)
    const exportAgentPDF = async () => {
      try {
        const response = await api.post('/quote-generator/export-agent-pdf/', {
          quote: generatedQuote.value
        });
        
        // Open HTML in new window for printing
        const printWindow = window.open('', '_blank');
        printWindow.document.write(response.data);
        printWindow.document.close();
        
        // Wait for content to load, then show print dialog
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            alert('PDF ready! Use your browser\'s print dialog to save as PDF.\n\nTip: Choose "Save as PDF" as the destination.');
          }, 500);
        };
      } catch (err) {
        console.error('Error generating agent PDF:', err);
        alert('Failed to generate PDF');
      }
    };
    
    // Save quote as draft (persistable, editable)
    const saveQuoteAsDraft = async () => {
      if (!generatedQuote.value) return;
      
      savingQuote.value = true;
      try {
        const response = await api.post('/quotes/', {
          generated_data: generatedQuote.value,
          generation_params: agentForm.value,
          quote_type: 'AGENT'
        });
        
        savedQuote.value = response.data.quote;
        console.log('Quote saved:', savedQuote.value.quote_number);
        
      } catch (err) {
        console.error('Error saving quote:', err);
        alert('Failed to save quote: ' + (err.response?.data?.error || err.message));
      } finally {
        savingQuote.value = false;
      }
    };
    
    // Navigate to quote editor
    const editSavedQuote = () => {
      if (savedQuote.value) {
        window.location.href = `/quote-editor/${savedQuote.value.id}`;
      }
    };
    
    // Export to markdown
    const exportMarkdown = async () => {
      try {
        // Determine export type
        let exportType = 'quote';
        if (generatedQuote.value.proposal_type === 'agent_pov') {
          exportType = 'agent_proposal';  // Agent-focused proof-of-value proposal
        } else if (generatedQuote.value.use_case_analysis) {
          exportType = 'proposal';
        } else if (generatedQuote.value.carrier_name) {
          exportType = 'carrier_client';  // Client-facing carrier quote with questionnaire
        }
        
        const response = await api.post('/quote-generator/export-markdown/', {
          quote: generatedQuote.value,
          type: exportType
        }, {
          responseType: 'blob'
        });
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${generatedQuote.value.quote_id}.md`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (err) {
        console.error('Error exporting markdown:', err);
        alert('Failed to export markdown');
      }
    };
    
    // Copy to clipboard
    const copyToClipboard = () => {
      const summary = generatedQuote.value.summary;
      navigator.clipboard.writeText(summary).then(() => {
        alert('Summary copied to clipboard!');
      }).catch(err => {
        console.error('Failed to copy:', err);
      });
    };
    
    // Print quote
    const printQuote = () => {
      window.print();
    };
    
    // Format check type for display
    const formatCheckType = (checkType) => {
      if (!checkType) return '';
      return checkType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };
    
    onMounted(() => {
      loadFeatureCatalog();
      loadSubscriptionTiers();
      loadAvailableCheckDefinitions();
    });
    
    return {
      activeTab,
      loading,
      error,
      availableFeatures,
      subscriptionTiers,
      availableCheckDefinitions,
      loadingCheckDefs,
      simpleForm,
      proposalForm,
      compareForm,
      carrierForm,
      agentForm,
      generatedQuote,
      comparisonResults,
      savedQuote,
      savingQuote,
      generateQuote,
      generateProposal,
      generateCarrierQuote,
      generateAgentProposal,
      generateComparison,
      toggleUseCase,
      selectTierFromComparison,
      exportPDF,
      exportAgentPDF,
      exportMarkdown,
      copyToClipboard,
      printQuote,
      formatCheckType,
      saveQuoteAsDraft,
      editSavedQuote
    };
  }
};
</script>

<style scoped>
.quote-builder-page {
  /* Page chrome: global .entity-list-page */
  padding-bottom: 2.5rem;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.card {
  background: white;
  border-radius: 10px;
  padding: 1.25rem;
  margin-bottom: 1.25rem;
  border: 1px solid #e2e8f0;
  box-shadow: none;
}

.quick-actions {
  display: flex;
  gap: 1rem;
  padding: 1rem;
}

.quick-actions button {
  flex: 1;
  padding: 1rem;
  font-size: 1.1rem;
}

.quick-actions button.active {
  background: #007bff;
  color: white;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
}

.feature-selection, .use-case-selection {
  display: grid;
  gap: 1rem;
}

.feature-checkbox {
  padding: 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.feature-checkbox label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.feature-name {
  font-weight: 600;
}

.feature-description {
  margin: 0.5rem 0 0 2rem;
  color: #666;
  font-size: 0.9rem;
}

.use-case-card {
  padding: 1.5rem;
  border: 2px solid #ddd;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.use-case-card:hover {
  border-color: #007bff;
  box-shadow: 0 2px 8px rgba(0,123,255,0.2);
}

.use-case-card.selected {
  border-color: #007bff;
  background: #f0f8ff;
}

.use-case-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.use-case-header h4 {
  flex: 1;
  margin: 0;
}

.badge {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
}

.badge-primary {
  background: #007bff;
  color: white;
}

.badge-success {
  background: #28a745;
  color: white;
}

.competency-badge {
  background: #ffc107;
  color: #000;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
}

.use-case-problem {
  margin-bottom: 0.5rem;
  color: #666;
  font-size: 0.9rem;
}

.use-case-description {
  margin-bottom: 1rem;
  color: #333;
}

.value-props ul {
  margin: 0.5rem 0 0 1.5rem;
  font-size: 0.9rem;
}

.action-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
}

.btn {
  padding: 0.75rem 2rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
}

.btn-success {
  background: #28a745;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background: #1e7e34;
}

.btn-info {
  background: #17a2b8;
  color: white;
}

.btn-info:hover:not(:disabled) {
  background: #117a8b;
}

.btn-outline {
  background: white;
  color: #007bff;
  border: 1px solid #007bff;
}

.btn-outline:hover {
  background: #007bff;
  color: white;
}

.btn-lg {
  padding: 1rem 3rem;
  font-size: 1.1rem;
}

.btn-block {
  width: 100%;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.quote-summary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.quote-id {
  font-size: 0.9rem;
  opacity: 0.9;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
}

.summary-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.summary-item .label {
  font-size: 0.9rem;
  opacity: 0.9;
}

.summary-item .value {
  font-size: 1.5rem;
  font-weight: 700;
}

.summary-item .value.success {
  color: #90EE90;
}

.cost-breakdown h3, .cost-breakdown h4 {
  margin-bottom: 1rem;
}

.cost-section {
  margin-bottom: 2rem;
}

.cost-grid {
  display: grid;
  gap: 0.75rem;
}

.cost-item {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 4px;
}

.cost-item.total {
  background: #e9ecef;
  font-weight: 600;
  font-size: 1.1rem;
}

.use-case-costs {
  margin-top: 1.5rem;
}

.use-case-cost-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  border-bottom: 1px solid #eee;
}

.cost-value {
  font-weight: 600;
  color: #007bff;
}

.cost-detail {
  font-size: 0.9rem;
  color: #666;
}

.margin-analysis {
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 2px solid #eee;
}

.margin-item {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  font-size: 1.1rem;
}

.margin-item.success {
  background: #d4edda;
  color: #155724;
  font-weight: 700;
  border-radius: 4px;
  margin-top: 1rem;
}

.use-case-detail {
  padding: 2rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  margin-bottom: 2rem;
}

.problem-solution {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin: 1.5rem 0;
}

.problem, .solution {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 4px;
}

.value-delivered, .competitive-position {
  margin-top: 1.5rem;
}

.comp-grid {
  display: grid;
  gap: 1rem;
  margin-top: 1rem;
}

.comp-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 4px;
}

.timeline-summary {
  font-size: 1.1rem;
  margin-bottom: 2rem;
}

.timeline-phases {
  display: grid;
  gap: 1.5rem;
}

.phase {
  border-left: 4px solid #007bff;
  padding-left: 1.5rem;
}

.phase-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.phase-duration {
  color: #666;
  font-size: 0.9rem;
}

.phase-activities {
  list-style: none;
  padding: 0;
}

.phase-activities li {
  padding: 0.5rem 0;
  padding-left: 1.5rem;
  position: relative;
}

.phase-activities li:before {
  content: "✓";
  position: absolute;
  left: 0;
  color: #28a745;
  font-weight: bold;
}

.export-buttons {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.comparison-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
}

.tier-card {
  border: 2px solid #ddd;
}

.tier-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #eee;
}

.tier-pricing {
  text-align: center;
  margin-bottom: 2rem;
}

.price {
  font-size: 2rem;
  font-weight: 700;
  color: #007bff;
  margin-bottom: 0.5rem;
}

.setup-fee {
  color: #666;
  font-size: 0.9rem;
}

.tier-features {
  margin-bottom: 2rem;
}

.tier-features h4 {
  margin-bottom: 1rem;
}

.tier-features ul {
  list-style: none;
  padding: 0;
}

.tier-features li {
  padding: 0.5rem 0;
  padding-left: 1.5rem;
  position: relative;
}

.tier-features li:before {
  content: "✓";
  position: absolute;
  left: 0;
  color: #28a745;
}

.tier-roi {
  margin-bottom: 2rem;
}

.roi-item {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  border-bottom: 1px solid #eee;
}

.roi-item .success {
  color: #28a745;
  font-weight: 600;
}

.error-message {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.export-note {
  margin-top: 1rem;
  padding: 1rem;
  background: #d1ecf1;
  border-left: 4px solid #17a2b8;
  font-size: 0.9rem;
  color: #0c5460;
}

.saved-quote-info {
  margin-top: 1rem;
  padding: 1rem 1.5rem;
  background: #d4edda;
  border-left: 4px solid #28a745;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.95rem;
  color: #155724;
}

.saved-quote-info i {
  color: #28a745;
  font-size: 1.2rem;
}

.saved-quote-info .edit-link {
  margin-left: auto;
  color: #155724;
  font-weight: 600;
  text-decoration: none;
  transition: color 0.2s;
}

.saved-quote-info .edit-link:hover {
  color: #0a4014;
  text-decoration: underline;
}

.btn-primary {
  background: #667eea;
  color: white;
  font-weight: 600;
}

.btn-primary:hover:not(:disabled) {
  background: #5568d3;
}

.pain-point-selection {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 0.75rem;
  margin-top: 0.5rem;
}

.checkbox-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.checkbox-item:hover {
  background: #e9ecef;
}

.checkbox-item input[type="checkbox"] {
  width: 18px;
  height: 18px;
}

.checkbox-item input[type="checkbox"]:checked + span {
  color: #007bff;
  font-weight: 500;
}

/* Check Definition Selection */
.check-definition-selection {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 0.75rem;
  margin-top: 0.5rem;
}

.check-def-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  border: 2px solid #e9ecef;
}

.check-def-item:hover {
  border-color: #667eea;
}

.check-def-item input[type="checkbox"]:checked ~ .check-def-info {
  color: #667eea;
}

.check-def-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.check-def-name {
  font-weight: 600;
  color: #2c3e50;
}

.check-def-type {
  font-size: 0.8rem;
  color: #667eea;
  background: #e8ecfc;
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
  align-self: flex-start;
}

.field-help {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 0.75rem;
  font-style: italic;
}

.field-note {
  font-size: 0.85rem;
  color: #17a2b8;
  margin-top: 0.5rem;
}

.field-note i {
  margin-right: 0.25rem;
}

.agent-section {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.agent-section h3 {
  color: #2c3e50;
}

.agent-section .section-description {
  color: #555;
  margin-bottom: 2rem;
  font-size: 1.05rem;
  line-height: 1.6;
}

/* Case Study highlight in results */
.case-study-section {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 8px;
  padding: 2rem;
  margin-bottom: 2rem;
}

.case-study-section h4 {
  margin-bottom: 1rem;
  font-size: 1.3rem;
}

.case-study-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.case-study-stat {
  text-align: center;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
}

.case-study-stat .stat-value {
  font-size: 2rem;
  font-weight: 700;
  display: block;
}

.case-study-stat .stat-label {
  font-size: 0.9rem;
  opacity: 0.9;
}

/* Agent Proposal Results Styling */
.agent-proposal-results {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.case-study-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.case-study-header h3 {
  margin: 0;
  font-size: 1.5rem;
}

.case-study-badge {
  background: rgba(255, 255, 255, 0.2);
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
}

.case-study-intro {
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
  opacity: 0.95;
}

.testimonial-box {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 1.5rem;
  position: relative;
}

.testimonial-box i {
  font-size: 1.5rem;
  opacity: 0.5;
  margin-bottom: 0.5rem;
}

.testimonial-box p {
  font-style: italic;
  font-size: 1.1rem;
  line-height: 1.6;
  margin: 0.5rem 0;
}

.testimonial-author {
  display: block;
  text-align: right;
  font-size: 0.9rem;
  opacity: 0.9;
  margin-top: 1rem;
}

/* Pain Points Section */
.pain-points-section h3 {
  margin-bottom: 1.5rem;
  color: #2c3e50;
}

.pain-point-cards {
  display: grid;
  gap: 1.5rem;
}

.pain-point-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  border-left: 4px solid #667eea;
}

.pain-point-problem {
  margin-bottom: 1rem;
  color: #dc3545;
}

.pain-point-problem i {
  margin-right: 0.5rem;
}

.pain-point-solution {
  margin-bottom: 1rem;
  color: #28a745;
}

.pain-point-solution i {
  margin-right: 0.5rem;
}

.pain-point-value {
  color: #007bff;
}

.pain-point-value i {
  margin-right: 0.5rem;
}

.pain-point-card p {
  margin: 0.5rem 0 0 1.5rem;
  color: #333;
}

/* ROI Section */
.roi-section h3 {
  margin-bottom: 1.5rem;
  color: #2c3e50;
}

.roi-grid {
  display: grid;
  gap: 0.75rem;
}

.roi-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 4px;
}

.roi-item.highlight {
  background: #d4edda;
}

.roi-item.total {
  background: #e9ecef;
  font-weight: 600;
  font-size: 1.1rem;
}

.roi-item.success-bg {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
}

.roi-item .cost {
  color: #dc3545;
  font-weight: 600;
}

.roi-item .savings {
  color: #28a745;
  font-weight: 600;
}

.roi-item .value {
  font-weight: 700;
}

/* Next Steps Section */
.next-steps-section h3 {
  margin-bottom: 1.5rem;
  color: #2c3e50;
}

.next-steps-list {
  padding-left: 1.5rem;
}

.next-steps-list li {
  padding: 0.75rem 0;
  font-size: 1.05rem;
  border-bottom: 1px solid #eee;
}

.next-steps-list li:last-child {
  border-bottom: none;
}

/* Structured Next Steps (New format) */
.structured-next-steps {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.structured-step {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.25rem;
  border-left: 4px solid #667eea;
}

.step-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.75rem;
}

.step-badge {
  background: #667eea;
  color: white;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
}

.step-title {
  font-weight: 600;
  font-size: 1.1rem;
  color: #2c3e50;
  flex: 1;
}

.step-timeframe {
  color: #666;
  font-size: 0.9rem;
  background: #e9ecef;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
}

.step-actions {
  margin: 0;
  padding-left: 1.5rem;
}

.step-actions li {
  padding: 0.25rem 0;
  color: #555;
}

/* Check Definitions Section */
.check-definitions-section {
  background: #fff;
}

.check-definitions-section .section-intro {
  color: #666;
  margin-bottom: 1.5rem;
  font-size: 1rem;
  line-height: 1.6;
}

.check-definitions-grid {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.check-definition-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  border-left: 4px solid #667eea;
}

.check-def-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.75rem;
}

.check-def-header h4 {
  margin: 0;
  color: #667eea;
  font-size: 1.15rem;
  flex: 1;
}

.check-type-badge {
  background: #667eea;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
}

.check-def-description {
  color: #555;
  margin-bottom: 1rem;
  line-height: 1.5;
}

.sample-requirements {
  background: white;
  border-radius: 4px;
  padding: 1rem;
}

.sample-requirements strong {
  color: #333;
  display: block;
  margin-bottom: 0.5rem;
}

.sample-requirements ul {
  margin: 0;
  padding-left: 1.25rem;
}

.sample-requirements li {
  padding: 0.35rem 0;
  color: #555;
  font-size: 0.95rem;
}

.sample-requirements li strong {
  color: #667eea;
  display: inline;
}

/* Pilot Process Section */
.pilot-process-section {
  background: #fff;
}

.pilot-process-section .section-intro {
  color: #666;
  margin-bottom: 1.5rem;
  font-size: 1rem;
  line-height: 1.6;
  font-style: italic;
}

.pilot-steps {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;
}

.pilot-step {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.pilot-step .step-number {
  background: #667eea;
  color: white;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.pilot-step .step-content {
  flex: 1;
}

.pilot-step .step-content strong {
  color: #2c3e50;
  font-size: 1.05rem;
}

.pilot-step .step-content p {
  margin: 0.25rem 0 0 0;
  color: #555;
  line-height: 1.5;
}

.pilot-transparency-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
}

@media (max-width: 768px) {
  .pilot-transparency-grid {
    grid-template-columns: 1fr;
  }
}

.transparency-box {
  border-radius: 8px;
  padding: 1.25rem;
}

.transparency-box.share {
  background: #d4edda;
  border: 1px solid #c3e6cb;
}

.transparency-box.protect {
  background: #f8d7da;
  border: 1px solid #f5c6cb;
}

.transparency-box h4 {
  margin: 0 0 0.75rem 0;
  font-size: 1rem;
}

.transparency-box.share h4 {
  color: #155724;
}

.transparency-box.protect h4 {
  color: #721c24;
}

.transparency-box h4 i {
  margin-right: 0.5rem;
}

.transparency-box ul {
  margin: 0;
  padding-left: 1.25rem;
}

.transparency-box.share ul li {
  color: #155724;
  padding: 0.25rem 0;
}

.transparency-box.protect ul li {
  color: #721c24;
  padding: 0.25rem 0;
}

/* Sample Analysis Results Section */
.sample-results-section.card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  margin-bottom: 1.5rem;
}

.sample-results-section .section-intro {
  color: #666;
  margin-bottom: 1.5rem;
  line-height: 1.6;
}

.check-run-group {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
}

.check-run-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.check-run-header h4 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.2rem;
}

.check-run-description {
  color: #666;
  margin-bottom: 1rem;
  line-height: 1.5;
}

.sample-results-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.sample-result-card {
  background: white;
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid #e9ecef;
}

.sample-result-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.entity-name {
  font-weight: 600;
  color: #2c3e50;
}

.entity-name i {
  color: #667eea;
  margin-right: 0.5rem;
}

.outcome-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.85rem;
  text-transform: uppercase;
}

.outcome-badge.pass {
  background: #d4edda;
  color: #155724;
}

.outcome-badge.fail {
  background: #f8d7da;
  color: #721c24;
}

.outcome-badge.needs_review {
  background: #fff3cd;
  color: #856404;
}

.sample-result-stats {
  display: flex;
  gap: 1.5rem;
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.sample-result-stats i {
  margin-right: 0.35rem;
}

.sample-result-stats .passed {
  color: #28a745;
}

.sample-result-stats .failed {
  color: #dc3545;
}

.requirement-results-table {
  overflow-x: auto;
  margin-bottom: 1rem;
}

.requirement-results-table table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.requirement-results-table th {
  background: #667eea;
  color: white;
  padding: 0.5rem 0.75rem;
  text-align: left;
  font-weight: 600;
}

.requirement-results-table td {
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #e9ecef;
}

.requirement-results-table tr:nth-child(even) {
  background: #f8f9fa;
}

.result-cell {
  font-weight: 600;
}

.result-cell.pass {
  color: #28a745;
}

.result-cell.fail {
  color: #dc3545;
}

.result-cell i {
  margin-right: 0.35rem;
}

.severity-badge {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.severity-badge.critical {
  background: #dc3545;
  color: white;
}

.severity-badge.high {
  background: #fd7e14;
  color: white;
}

.severity-badge.medium {
  background: #ffc107;
  color: #333;
}

.severity-badge.low {
  background: #28a745;
  color: white;
}

.evidence-cell {
  font-size: 0.85rem;
  color: #555;
  max-width: 300px;
}

.more-row .more-cell {
  text-align: center;
  font-style: italic;
  color: #999;
}

.key-findings {
  background: #fff3cd;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  font-size: 0.9rem;
}

.key-findings strong {
  color: #856404;
  display: block;
  margin-bottom: 0.5rem;
}

.key-findings ul {
  margin: 0;
  padding-left: 1.25rem;
}

.key-findings li {
  padding: 0.25rem 0;
  color: #555;
}

.sample-results-footer {
  text-align: center;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
  color: #666;
  font-size: 0.9rem;
}

.sample-results-footer p {
  margin: 0.5rem 0;
}

.sample-results-footer i {
  margin-right: 0.5rem;
  color: #667eea;
}

/* Report Preview Cards */
.sample-reports-summary {
  margin-top: 1rem;
}

.reports-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.report-preview-card {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 1rem;
  transition: transform 0.2s, box-shadow 0.2s;
}

.report-preview-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.report-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.report-number {
  font-weight: 600;
  color: #667eea;
  font-size: 0.9rem;
}

.report-entity {
  font-size: 0.95rem;
  color: #2c3e50;
  margin-bottom: 0.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e9ecef;
}

.report-entity i {
  color: #667eea;
  margin-right: 0.5rem;
}

.report-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
  text-align: center;
  margin-bottom: 0.75rem;
}

.report-stats .stat {
  padding: 0.5rem 0;
}

.report-stats .stat-value {
  display: block;
  font-size: 1.25rem;
  font-weight: 700;
  color: #2c3e50;
}

.report-stats .stat.passed .stat-value {
  color: #28a745;
}

.report-stats .stat.failed .stat-value {
  color: #dc3545;
}

.report-stats .stat-label {
  display: block;
  font-size: 0.7rem;
  color: #999;
  text-transform: uppercase;
}

.report-date {
  font-size: 0.85rem;
  color: #999;
  text-align: center;
}

.report-date i {
  margin-right: 0.35rem;
}

/* Form section for sample results selection */
.form-group.sample-results-section {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 1.25rem;
  border-radius: 10px;
  border: 1px solid #dee2e6;
  margin-bottom: 1.5rem;
}

.sample-results-selection {
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: white;
  padding: 0.5rem;
}

.check-def-meta {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.completed-runs {
  font-size: 0.8rem;
  color: #28a745;
  background: #d4edda;
  padding: 2px 8px;
  border-radius: 10px;
}

.loading-indicator,
.no-data-message {
  padding: 1.5rem;
  text-align: center;
  color: #666;
  background: white;
  border-radius: 8px;
  border: 1px dashed #dee2e6;
}

.loading-indicator i {
  margin-right: 0.5rem;
  color: #667eea;
}

.no-data-message i {
  margin-right: 0.5rem;
  color: #999;
}

.field-note.success-note {
  color: #155724;
  background: #d4edda;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
}

.field-note.success-note i {
  color: #28a745;
}

@media (max-width: 768px) {
  .sample-result-stats {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .requirement-results-table {
    font-size: 0.8rem;
  }
  
  .evidence-cell {
    max-width: 150px;
  }
}

@media print {
  .quick-actions,
  .action-buttons,
  .export-section {
    display: none;
  }
}
</style>

